import type { AxiosError } from "axios";

import { PositionRole, type DefaultApi } from "@/api";
import { OrganizationMember } from "@/models/organization_member";
import { OrganizationMembers } from "@/models/organization_members";
import type { State as RootState } from "@/stores";
import type { Module } from "vuex";
export interface State {
  organizationMembers?: OrganizationMembers;
  // Separate from the active list so shared consumers of `organizationMembers` stay active-only.
  deactivatedMembers?: OrganizationMembers;
  // user_id -> [position_id, ...] for the role requested at fetch time.
  positionMemberships: Record<string, number[]>;
}
export const module: Module<State, RootState> = {
  namespaced: true,
  state: {
    organizationMembers: undefined,
    deactivatedMembers: undefined,
    positionMemberships: {},
  },
  actions: {
    async getOrganizationMembers({ commit }) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      try {
        const response = await api.getOrganizationMembers();
        const organizationMembers = new OrganizationMembers(
          response.data.map(
            (member: Partial<OrganizationMember>) =>
              new OrganizationMember(member),
          ),
        );
        commit("setOrganizationMembers", organizationMembers);
        return organizationMembers;
      } catch (error) {
        // This endpoint is admin-only, and PositionList/AutomationSetupStep read the cached roster to
        // decide whether the current user is an admin -- so a refusal must not leave a stale list that
        // still says they are. A transient failure keeps it: blanking there drops the Members screen's
        // selection, which switches an open edit panel into invitation mode.
        const status = (error as AxiosError).response?.status;
        if (status === 401 || status === 403) {
          commit("setOrganizationMembers", undefined);
        }
        throw error;
      }
    },
    async postOrganizationRole({}, userId: string) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.postOrganizationRole(userId);
      return response.data;
    },
    async deleteOrganizationRole({}, userId: string) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.deleteOrganizationRole(userId);
      return response.data;
    },
    async updateMemberName(
      {},
      {
        userId,
        firstName,
        lastName,
      }: { userId: string; firstName: string; lastName: string },
    ): Promise<void> {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      await api.updateMemberProfile(userId, {
        first_name: firstName,
        last_name: lastName,
      });
    },
    async getDeactivatedMembers({ commit }) {
      const api = (await this.dispatch("getAuthorizedApi", null, {
        root: true,
      })) as DefaultApi;
      const response = await api.getDeactivatedOrganizationMembers();
      const deactivatedMembers = new OrganizationMembers(
        response.data.map(
          (member: Partial<OrganizationMember>) =>
            new OrganizationMember(member),
        ),
      );
      commit("setDeactivatedMembers", deactivatedMembers);
      return deactivatedMembers;
    },
    async deactivateOrganizationMember({ dispatch }, userId: string) {
      const api = (await this.dispatch("getAuthorizedApi", null, {
        root: true,
      })) as DefaultApi;
      await api.deactivateOrganizationMember(userId);
      // Refetch both; the UI dedupes a lagging active list against the inactive memberships.
      await Promise.all([
        dispatch("getOrganizationMembers"),
        dispatch("getDeactivatedMembers"),
      ]);
    },
    async activateOrganizationMember({ dispatch }, userId: string) {
      const api = (await this.dispatch("getAuthorizedApi", null, {
        root: true,
      })) as DefaultApi;
      await api.activateOrganizationMember(userId);
      await Promise.all([
        dispatch("getOrganizationMembers"),
        dispatch("getDeactivatedMembers"),
      ]);
    },
    async getUserPositions(
      _ctx,
      {
        userId,
        role,
      }: {
        userId: string;
        role?: PositionRole;
      },
    ): Promise<number[]> {
      const api = (await this.dispatch("getAuthorizedApi", null, {
        root: true,
      })) as DefaultApi;
      const response = await api.getUserPositions(userId, role);
      return response.data;
    },
    async getPositionMemberships(
      { commit },
      role?: PositionRole,
    ): Promise<Record<string, number[]>> {
      const api = (await this.dispatch("getAuthorizedApi", null, {
        root: true,
      })) as DefaultApi;
      const response = await api.getPositionMemberships(role);
      const memberships = response.data as Record<string, number[]>;
      commit("setPositionMemberships", memberships);
      return memberships;
    },
    async postOrganizationPermission(
      {},
      {
        userId,
        positionId,
        role,
      }: {
        userId: string;
        positionId: number;
        role: PositionRole;
      },
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.postOrganizationPermission(
        positionId,
        userId,
        role,
      );
      return response.data;
    },
    async deleteOrganizationPermission(
      {},
      {
        userId,
        positionId,
        role,
      }: {
        userId: string;
        positionId: number;
        role: PositionRole;
      },
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.deleteOrganizationPermission(
        positionId,
        userId,
        role,
      );
      return response.data;
    },
    async getPositionAdminUserIds(_ctx, positionId: number): Promise<string[]> {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.getPositionUsers(positionId);
      return response.data;
    },
  },
  mutations: {
    setOrganizationMembers(state, organizationMembers: OrganizationMembers) {
      state.organizationMembers = organizationMembers;
    },
    setDeactivatedMembers(state, deactivatedMembers: OrganizationMembers) {
      state.deactivatedMembers = deactivatedMembers;
    },
    setPositionMemberships(state, memberships: Record<string, number[]>) {
      state.positionMemberships = memberships;
    },
  },
};
